/* eslint-disable */
// ######====== IMPORTS GENERAL
// Import Basic
import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import 'moment/locale/es'
import 'moment/locale/pt'
import { toast } from 'react-toastify'
import { Slide as ToastSlide } from 'react-toastify'
import { ThreeDots } from 'react-loader-spinner'

// Import Material-UI
import { 
  Typography, 
  Snackbar, 
  IconButton, 
  Tooltip, 
  Dialog, 
  DialogTitle, 
  DialogContent
} from '@material-ui/core'
import { EmojiObjects, Close, FileCopy, Mic } from '@material-ui/icons'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import PerfectScrollbar from 'react-perfect-scrollbar'
import 'react-perfect-scrollbar/dist/css/styles.css'
import CustomLinearProgress from '../../components/CustomLinearProgress/CustomLinearProgress'

// Import Internal components
import AuthContext from '../../contexts/AuthProvider.js'
import { client } from '../../services/axiosClient'
import { generateInitiativeInsights } from '../../services/aiApi'
import InitiativeInsightsLoading from '../../components/InitiativeInsightsLoading/InitiativeInsightsLoading'
import AIInsightsModal from '../../components/AI/AIInsightsModal'

// Import OnePage components
import OnePageAppBar from '../../components/OnePage/OnePage-AppBar' // top app bar for selecting date, edit, save
import OnePageApprovalBanner from '../../components/OnePage/OnePage-ApprovalBanner' // approval banner status
import OnePageContentContainer from '../../components/OnePage/OnePage-ContentContainer' // Containerized component (does NOT have local states, uses values and update functions passed via props)
import OnePageDrawerApprovals from '../../components/OnePage/OnePage-DrawerApprovals' // approvals drawer
import OnePageSpeechRecorder from '../../components/OnePage/OnePage-SpeechRecorder' // speech-to-text recorder
import { performOnePageApprovalAction } from 'services/onePageApi'

//////////////////////////////////////////////////////////////////
// AUTOSAVE CONSTANTS AND HELPERS
//////////////////////////////////////////////////////////////////
const AUTOSAVE_KEY_PREFIX = 'onepage_draft_'
const AUTOSAVE_EXPIRATION_HOURS = 2

// Generate unique autosave key for each initiative
const getAutosaveKey = (initiativeId) => {
  return `${AUTOSAVE_KEY_PREFIX}${initiativeId}`
}

//////////////////////////////////////////////////////////////////
// Main component OnePage
//////////////////////////////////////////////////////////////////
const OnePage = ({
  // ######====== COMPONENT PROPS
  selectedInitiative,
  onePageInfoData,
  setOnePageInfoData,
  fetchOnePageData,
  onePageHasApproval,
  t,
  classes,
  canReport,
  canApprove,
  showContent = false,
  defaultOnePageDate = ''
}) => {
  //  Setup of User and Language - MUST BE FIRST
  const { userDetails } = useContext(AuthContext)
  
  // ALL useState HOOKS MUST BE HERE BEFORE ANY EARLY RETURNS
  const [newCustomFields, setNewCustomFields] = useState({})
  const [isCreatingOnePage, setIsCreatingOnePage] = useState(false)
  const [isEditingOnePage, setIsEditingOnePage] = useState(false)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [selectedApproval, setSelectedApproval] = useState(null)
  const [selectedOnePageDate, setSelectedOnePageDate] = useState(defaultOnePageDate)
  const [onePageDateList, setOnePageDateList] = useState([])
  const [currentOnePage, setCurrentOnePage] = useState(null)
  const [lastOnePages, setLastOnePages] = useState(null)
  const [isOnePageLoading, setIsOnePageLoading] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [newTendency, setNewTendency] = useState('')
  const [newDate, setNewDate] = useState(moment().format('YYYY-MM-DD'))
  const [newKm, setNewKm] = useState('')
  const [newAch, setNewAch] = useState('')
  const [newNextSteps, setNewNextSteps] = useState('')
  const [newHelpRequests, setNewHelpRequests] = useState('')
  const [isApproving, setIsApproving] = useState(false)
  const [isApproveDrawerOpen, setIsApproveDrawerOpen] = useState(false)
  const [approveComment, setApproveComment] = useState('')
  const [openApproverNotification, setOpenApproverNotification] = useState(true)
  const [progress, setProgress] = useState(100)
  const [isCopying, setIsCopying] = useState(false)
  const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false)
  const [insightsContent, setInsightsContent] = useState('')
  const [isLoadingInsights, setIsLoadingInsights] = useState(false)
  const [isSpeechRecorderOpen, setIsSpeechRecorderOpen] = useState(false)
  const [speechProcessedData, setSpeechProcessedData] = useState(null)
  const [textEditorKey, setTextEditorKey] = useState(0) // Para forçar re-render dos TextEditors
  const [lastAutosaveTime, setLastAutosaveTime] = useState(null) // Track last autosave timestamp
  
  // ALL useEffect HOOKS MUST BE HERE TOO
  useEffect(() => {
    if (defaultOnePageDate && defaultOnePageDate !== selectedOnePageDate) {
      setSelectedOnePageDate(defaultOnePageDate)
    } else if (!selectedOnePageDate && onePageInfoData && onePageInfoData.length > 0) {
      // If no date is selected and we have data, automatically select the most recent
      const mostRecentDate = onePageInfoData
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date
      if (mostRecentDate) {
        setSelectedOnePageDate(mostRecentDate)
      }
    }
  }, [defaultOnePageDate, selectedOnePageDate, onePageInfoData])

  // Set Current OnePage Data
  useEffect(() => {
    console.log("[DEBUG] OnePage_v2: Setting current OnePage data", {
      onePageInfoData,
      selectedOnePageDate,
      dataLength: onePageInfoData?.length
    })
    
    setCurrentOnePage(null)

    if (onePageInfoData && Array.isArray(onePageInfoData)) {
      const dateOptions = onePageInfoData
        .map(item => ({
          date: item.date,
          approval_status: item.approval_status || 'approved'
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date))

      setOnePageDateList(dateOptions)

      // Find the current OnePage based on the selected date string
      let foundOnePage = onePageInfoData.find(info => info.date === selectedOnePageDate)
      console.log("[DEBUG] OnePage_v2: Found OnePage by date", { foundOnePage, selectedOnePageDate })
      
      // If not found and we have data, use the most recent OnePage (first in sorted array)
      if (!foundOnePage && onePageInfoData.length > 0) {
        const sortedData = onePageInfoData.sort((a, b) => new Date(b.date) - new Date(a.date))
        foundOnePage = sortedData[0]
        console.log("[DEBUG] OnePage_v2: Using most recent OnePage", { foundOnePage })
        // Also update the selectedOnePageDate to match the most recent
        setSelectedOnePageDate(foundOnePage.date)
      }
      
      console.log("[DEBUG] OnePage_v2: Final currentOnePage set to", foundOnePage)
      setCurrentOnePage(foundOnePage)
    }
  }, [onePageInfoData, selectedOnePageDate])

  // Set status of last 3 "onepages" {date, status, ...}
  useEffect(() => {
    console.log("[DEBUG] OnePage_v2: currentOnePage changed", { currentOnePage })
    setLastOnePages(null)

    if (currentOnePage) {
      const sortedOnePages = onePageInfoData //Mock substituido por Data
        .filter(op => op.date < currentOnePage.date) // ou se basear no ID, etc.
        .sort((a, b) => new Date(a.date) - new Date(b.date))

      setLastOnePages(sortedOnePages.slice(-3))
      console.log("[DEBUG] OnePage_v2: lastOnePages set", { lastOnePages: sortedOnePages.slice(-3) })
    }
  }, [currentOnePage, onePageInfoData])

  // Updates notification progress bar (3 seconds) - OPTIMIZED VERSION
  useEffect(() => {
    // Only run if notification should be open and has approval
    if (!openApproverNotification || !onePageHasApproval) {
      return
    }

    // Use a larger interval to reduce processing frequency
    const interval = 200 // Increased from 100ms to 200ms
    const duration = 3000
    const decrement = 100 / (duration / interval)
    
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev <= 0) {
          clearInterval(timer)
          setOpenApproverNotification(false)
          return 0
        }
        return Math.max(0, prev - decrement) // Prevent negative values
      })
    }, interval)

    return () => {
      clearInterval(timer)
    }
  }, [openApproverNotification, onePageHasApproval])

  // Auto-save to localStorage during edit/creation
  useEffect(() => {
    // Only autosave when in edit/creation mode
    if (!isCreatingOnePage || !selectedInitiative?.id) {
      return
    }

    const autosaveData = {
      status: newStatus,
      tendency: newTendency,
      date: newDate,
      km: newKm,
      achievements: newAch,
      next_steps: newNextSteps,
      help_requests: newHelpRequests,
      custom_fields: newCustomFields,
      timestamp: new Date().toISOString(),
      isEditing: isEditingOnePage,
      onePageId: currentOnePage?.id || null
    }

    try {
      localStorage.setItem(
        getAutosaveKey(selectedInitiative.id),
        JSON.stringify(autosaveData)
      )
      setLastAutosaveTime(new Date())
      console.log('[AUTOSAVE] Draft saved successfully at', new Date().toISOString())
    } catch (error) {
      console.error('[AUTOSAVE] Failed to save draft:', error)
    }
  }, [
    newStatus, newTendency, newDate, newKm, newAch, 
    newNextSteps, newHelpRequests, newCustomFields,
    isCreatingOnePage, isEditingOnePage, selectedInitiative?.id, currentOnePage?.id
  ])

  // Periodic token refresh to prevent expiration during long editing sessions
  useEffect(() => {
    // Only refresh token when in edit/creation mode
    if (!isCreatingOnePage) {
      return
    }

    // Refresh token every 4 minutes (before the 5-minute expiration)
    const tokenRefreshInterval = setInterval(async () => {
      try {
        await client.post('/api/awake/')
      } catch (error) {
        // If refresh fails with 401, warn user to save
        if (error.response?.status === 401) {
          toast.warning(
            t('warnings.session_expiring') || 
            'Sua sessão está expirando. Salve seu trabalho!',
            { autoClose: 10000 }
          )
        }
      }
    }, 4 * 60 * 1000) // Every 4 minutes

    return () => clearInterval(tokenRefreshInterval)
  }, [isCreatingOnePage, t])

  // Warn user before closing/refreshing page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isCreatingOnePage) {
        e.preventDefault()
        e.returnValue = '' // Chrome requires this
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isCreatingOnePage])

  // ALL useCallback HOOKS MUST BE HERE (MOVED FROM BELOW TO FIX HOOK ORDER)
  const handleOpenInsights = useCallback(async () => {
    if (!selectedInitiative?.id) {
      toast.error(t('insights.no_initiative_selected'))
      return
    }

    setIsInsightsModalOpen(true)
    setIsLoadingInsights(true)
    setInsightsContent('')

    try {
      const response = await generateInitiativeInsights(selectedInitiative.id)
      
      if (response.success) {
        setInsightsContent(response.text)
      } else {
        setInsightsContent(response.text || t('insights.error_loading'))
        toast.error(t('insights.error_loading'))
      }
    } catch (error) {
      console.error('Error fetching insights:', error)
      setInsightsContent(t('insights.error_loading'))
      toast.error(t('insights.error_loading'))
    } finally {
      setIsLoadingInsights(false)
    }
  }, [selectedInitiative?.id, t])

  const handleCloseInsights = useCallback(() => {
    setIsInsightsModalOpen(false)
    setInsightsContent('')
  }, [])

  const handleCopyToClipboard = useCallback(async () => {
    if (!insightsContent) return
    
    setIsCopying(true)
    try {
      await navigator.clipboard.writeText(insightsContent)
      toast.success(t('insights.copied_success'))
    } catch (error) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea')
      textArea.value = insightsContent
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      toast.success(t('insights.copied_success'))
    } finally {
      setIsCopying(false)
    }
  }, [insightsContent, t])

  const handleOpenSpeechRecorder = useCallback(() => {
    if (!isCreatingOnePage) {
      toast.warning(t('speech.edit_mode_required'))
      return
    }
    setIsSpeechRecorderOpen(true)
  }, [isCreatingOnePage, t])

  const handleCloseSpeechRecorder = useCallback(() => {
    setIsSpeechRecorderOpen(false)
    setSpeechProcessedData(null)
  }, [])

  const handleSpeechProcessed = useCallback((speechData) => {
    setSpeechProcessedData(speechData)
    
    // Processar conteúdo organizado e preencher campos (seguindo padrão do ControlPanel)
    const { organizedContent } = speechData
    
    if (organizedContent) {
      // Mapeamento dos campos padrão da One Page
      const fieldMappings = {
        // Campos principais (preferidos)
        'achievements': 'setNewAch',
        'next_steps': 'setNewNextSteps', 
        'help_requests': 'setNewHelpRequests',
        'km': 'setNewKm',
        'key_message': 'setNewKm', // Key messages vão para km
        'discussion_points': 'setNewHelpRequests', // Discussion points podem ir para help_requests
        
        // Campos combinados (backup compatibility) 
        'achievements_and_next_steps': 'setNewAch', // Fallback se vier combinado
        
        // Mapeamento adicional para chaves antigas (backup compatibility)
        'initiative_selected.one_page_key_messages': 'setNewKm',
        'initiative_selected.one_page_points_discussion': 'setNewHelpRequests',
        'initiative_selected.one_page_achievements_and_next_steps': 'setNewAch'
      }
      
      // Atualizar campos padrão
      Object.keys(organizedContent).forEach(key => {
        const content = organizedContent[key]
        
        if (!content) return // Pular campos vazios
        
        // Mapear para o setter correto
        if (fieldMappings[key]) {
          const setterName = fieldMappings[key]
          
          switch (setterName) {
            case 'setNewAch':
              setNewAch(content)
              break
            case 'setNewNextSteps':
              setNewNextSteps(content)
              break
            case 'setNewHelpRequests':
              setNewHelpRequests(content)
              break
            case 'setNewKm':
              setNewKm(content)
              break
          }
        }
      })
      
      // Processar campos customizados (custom:xxx)
      const customFields = {}
      Object.keys(organizedContent).forEach(key => {
        if (key.startsWith('custom:') && organizedContent[key]) {
          customFields[key] = organizedContent[key]
        }
      })
      
      if (Object.keys(customFields).length > 0) {
        setNewCustomFields(prev => ({
          ...prev,
          ...customFields
        }))
      }
      
      // Forçar re-render dos TextEditors incrementando um contador
      setTextEditorKey(prev => prev + 1)
      
      toast.success(t('speech.content_filled_success'))
    }
  }, [t])

  const renderStatusColor = useCallback((status) => {
    switch (status) {
      case 'red':
        return '#E04F5F'
      case 'green':
        return '#4caf50'
      case 'yellow':
        return '#ffbb00'
      case 'gray':
      default:
        return '#9e9e9e'
    }
  }, [])

  // Helper function to get the most recent OnePage for pre-filling
  const getMostRecentOnePage = useCallback(() => {
    if (!onePageInfoData || !Array.isArray(onePageInfoData) || onePageInfoData.length === 0) {
      return null
    }
    // Sort by date descending and get the most recent
    const sortedPages = [...onePageInfoData].sort((a, b) => new Date(b.date) - new Date(a.date))
    return sortedPages[0]
  }, [onePageInfoData])

  // Check and restore autosaved draft if available
  const checkAndRestoreAutosave = useCallback(() => {
    if (!selectedInitiative?.id) return null

    const autosaveKey = getAutosaveKey(selectedInitiative.id)
    const savedData = localStorage.getItem(autosaveKey)
    
    if (!savedData) return null

    try {
      const parsed = JSON.parse(savedData)
      const savedTime = new Date(parsed.timestamp)
      const now = new Date()
      const diffHours = (now - savedTime) / 1000 / 60 / 60

      // If draft is less than AUTOSAVE_EXPIRATION_HOURS old, offer to restore
      if (diffHours < AUTOSAVE_EXPIRATION_HOURS) {
        return parsed
      } else {
        // Clean up old drafts
        localStorage.removeItem(autosaveKey)
        console.log('[AUTOSAVE] Removed expired draft')
        return null
      }
    } catch (error) {
      console.error('[AUTOSAVE] Failed to parse saved draft:', error)
      localStorage.removeItem(autosaveKey)
      return null
    }
  }, [selectedInitiative?.id])

  // ALL useMemo HOOKS MUST BE HERE (MOVED FROM BELOW TO FIX HOOK ORDER)
  const aiInsightsButton = useMemo(() => {
    // Only render if initiative has valid ID and showContent is true
    if (!selectedInitiative?.id || !showContent) {
      return null
    }

    return (
      <div
        style={{
          position: 'fixed',
          right: 16,
          bottom: 80, // Positioned above other floating elements
          zIndex: 1300,
        }}
      >
        <Tooltip title={t('insights.button_tooltip')} placement="left">
          <IconButton
            style={{
              background: userDetails?.implementation_color || '#1976d2',
              color: '#fff',
              border: '1px solid #ccc',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              width: 40,
              height: 40,
            }}
            onClick={handleOpenInsights}
          >
            <EmojiObjects style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </div>
    )
  }, [selectedInitiative?.id, showContent, userDetails?.implementation_color, t, handleOpenInsights])

  const speechRecorderButton = useMemo(() => {
    // Only render if initiative has valid ID, showContent is true, and canReport is true
    if (!selectedInitiative?.id || !showContent || !canReport) {
      return null
    }

    return (
      <div
        style={{
          position: 'fixed',
          right: 16,
          bottom: 130, // Positioned above the insights button
          zIndex: 1300,
        }}
      >
        <Tooltip title={t('speech.button_tooltip')} placement="left">
          <IconButton
            style={{
              background: isCreatingOnePage 
                ? (userDetails?.implementation_color || '#1976d2')
                : '#9e9e9e',
              color: '#fff',
              border: '1px solid #ccc',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              width: 40,
              height: 40,
              opacity: isCreatingOnePage ? 1 : 0.7,
            }}
            onClick={handleOpenSpeechRecorder}
            disabled={!isCreatingOnePage}
          >
            <Mic style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </div>
    )
  }, [selectedInitiative?.id, showContent, canReport, userDetails?.implementation_color, isCreatingOnePage, t, handleOpenSpeechRecorder])
  
  // Performance monitoring (can be removed in production)
  const renderStartTime = performance.now()
  
  // DEBUG: Log rendering state (disabled for production)
  // console.warn('🔧 [OnePage_v2] Rendering with:', {
  //   selectedInitiative: selectedInitiative ? { id: selectedInitiative.id, initiative: selectedInitiative.initiative } : 'null',
  //   onePageInfoData: onePageInfoData ? `Array[${onePageInfoData.length}]` : String(onePageInfoData),
  //   showContent,
  //   isTemp: selectedInitiative?.initiative === "Loading...",
  //   timestamp: new Date().toISOString()
  // })
  
  // Loading state - show loader while data is being fetched
  // Only show error if onePageInfoData is explicitly not an array (not null/undefined)
  if (onePageInfoData !== null && onePageInfoData !== undefined && !Array.isArray(onePageInfoData)) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Typography>{t('onepage.errors.invalid_data')}</Typography>
      </div>
    )
  }

  // Show loading state when data is null/undefined (still loading) OR when initiative is temporary
  if (onePageInfoData === null || onePageInfoData === undefined || selectedInitiative?.initiative === "Loading...") {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px'
      }}>
        <ThreeDots
          color={userDetails?.implementation_color || '#1976d2'}
          height={60}
          width={60}
          ariaLabel="loading"
        />

      </div>
    )
  }

  // ######====== COMPONENT VARIABLES DECLARATION
  // HOOKS MOVED TO TOP TO FIX HOOK ORDER VIOLATION
  const safeOnePage = currentOnePage ?? {}
  
  // ALL REMAINING VARIABLES AND FUNCTIONS (NO MORE HOOKS BELOW THIS POINT)
  // maintained logic of custom fields but not implemented yet because of low variation of these fields.. if necessary we can implement in the future
  const currentLanguage = t(t)
  moment.locale(currentLanguage)
  //console.log("test rendering of the component")

  // ######====== HOOKS AND HANDLING FUNCTIONS
  // Cancel dialog
  const handleOpenCancelDialog = () => setIsCancelDialogOpen(true)
  const handleCloseCancelDialog = () => setIsCancelDialogOpen(false)
  const handleConfirmCancel = () => {
    setIsCreatingOnePage(false)
    setIsEditingOnePage(false)
    setIsCancelDialogOpen(false)
  }

  //Creation/Edition Functions
  const handleCreateOnePage = () => {
    // First, check if there's an autosaved draft
    const autosaveData = checkAndRestoreAutosave()
    
    if (autosaveData && !autosaveData.isEditing) {
      // Show confirmation dialog to restore draft
      const savedAgo = moment(autosaveData.timestamp).fromNow()
      const shouldRestore = window.confirm(
        t('onepage.restore_draft_message', { time: savedAgo }) || 
        `Encontramos um rascunho salvo ${savedAgo}. Deseja restaurá-lo?`
      )
      
      if (shouldRestore) {
        setIsCreatingOnePage(true)
        setIsEditingOnePage(false)
        setNewStatus(autosaveData.status || '')
        setNewTendency(autosaveData.tendency || '')
        setNewKm(autosaveData.km || '')
        setNewAch(autosaveData.achievements || '')
        setNewNextSteps(autosaveData.next_steps || '')
        setNewHelpRequests(autosaveData.help_requests || '')
        setNewCustomFields(autosaveData.custom_fields || {})
        setNewDate(moment().format('YYYY-MM-DD')) // Always use today's date
        setTextEditorKey(prev => prev + 1) // Force re-render
        
        toast.info(t('onepage.draft_restored') || 'Rascunho restaurado com sucesso!')
        return
      }
    }
    
    // Standard flow: no autosave or user declined
    setIsCreatingOnePage(true)
    setIsEditingOnePage(false)
    
    // Find the most recent OnePage to pre-fill content (UX improvement)
    const mostRecentOnePage = getMostRecentOnePage()

    if (mostRecentOnePage) {
      // Pre-fill with most recent OnePage content (only non-empty fields)
      setNewStatus(mostRecentOnePage.status || '')
      setNewTendency(mostRecentOnePage.tendency || '')
      setNewKm(mostRecentOnePage.km || '')
      setNewAch(mostRecentOnePage.achievements || '')
      setNewNextSteps(mostRecentOnePage.next_steps || '')
      setNewHelpRequests(mostRecentOnePage.help_requests || '')

      // Pre-fill custom fields from layout configuration
      const customData = {}
      const layout = userDetails.one_page_layout || []
      layout.forEach(section => {
        if (section.key.startsWith('custom:')) {
          customData[section.key] = mostRecentOnePage[section.key] || ''
        }
      })
      setNewCustomFields(customData)

      // Notify user that content was pre-filled
      toast.info(t('onepage.prefilled_from_last', { date: moment(mostRecentOnePage.date).format('DD/MM/YYYY') }) || `Conteúdo pré-preenchido da OnePage de ${moment(mostRecentOnePage.date).format('DD/MM/YYYY')}`)
    } else {
      // No previous OnePage found, start with empty fields
      setNewStatus('')
      setNewTendency('')
      setNewKm('')
      setNewAch('')
      setNewNextSteps('')
      setNewHelpRequests('')
      setNewCustomFields({})
    }

    // Always set new date to today regardless of pre-fill
    setNewDate(moment().format('YYYY-MM-DD'))
  }

  const handleEditOnePage = () => {
    if (currentOnePage) {
      //Business rule validation
      if (currentOnePage.review_meeting) {
        toast.warn(t('onepage.errors.edit_locked_review'))
        return
      }

      const onePageDate = moment(currentOnePage.date)
      const oneWeekAgo = moment().subtract(7, 'days')
      if (onePageDate.isBefore(oneWeekAgo)) {
        toast.warn(t('onepage.errors.edit_locked_old'))
        return
      }
      setNewStatus(currentOnePage.status)
      setNewTendency(currentOnePage.tendency)
      setNewDate(currentOnePage.date)
      setNewKm(currentOnePage.km)
      setNewAch(currentOnePage.achievements)
      setNewNextSteps(currentOnePage.next_steps)
      setNewHelpRequests(currentOnePage.help_requests)

      const customData = {}
      const layout = userDetails.one_page_layout || []
      layout.forEach(section => {
        if (section.key.startsWith('custom:')) {
          customData[section.key] = currentOnePage[section.key] || ''
        }
      })
      setNewCustomFields(customData)

      setIsEditingOnePage(true)
      setIsCreatingOnePage(true)
    }
  }

  const handleSaveNewOnePage = async () => {
    setIsOnePageLoading(true)

    if (!newStatus) {
      notify('Selecione um status antes de salvar.', 'error')
      setIsOnePageLoading(false)
      return
    }
    const isDuplicateDate = onePageDateList.includes(newDate)
    if (!isEditingOnePage && isDuplicateDate) {
      notify(
        'Não é possível criar OnePage com a mesma data de outra já existente',
        'error'
      )
      setIsOnePageLoading(false)
      return
    }

    const onePageData = {
      action: isEditingOnePage ? 'edit' : 'create',
      initiative_id: selectedInitiative.id,
      onepage_id: currentOnePage ? currentOnePage.id : null,
      status: newStatus,
      tendency: newTendency,
      date: newDate,
      km: newKm,
      next_steps: newNextSteps,
      help_requests: newHelpRequests,
      achievements: newAch,
      ...newCustomFields
    }

    try {
      // ✅ FIRST: Ensure token is valid by calling awake endpoint
      // This prevents 401 errors during the actual save operation
      const awakeResponse = await client.post('/api/awake/')
      
      // Verify the awake call actually succeeded
      if (awakeResponse.status !== 200) {
        throw new Error('Token validation failed')
      }
      
      // ✅ THEN: Perform the actual save with guaranteed valid token
      const response = await client.post(
        '/api/initiative-onepage-save/',
        onePageData
      )
      
      if (response.data.success) {
        notify(t('actions.save_success'), 'success')
        
        // ✅ Clear autosave after successful save
        if (selectedInitiative?.id) {
          localStorage.removeItem(getAutosaveKey(selectedInitiative.id))
        }
        
        await fetchOnePageData()
        setSelectedOnePageDate(newDate)
      } else {
        notify(t('actions.save_error'), 'error')
      }
    } catch (error) {
      // ✅ Better error handling for different error types
      if (error.response?.status === 401) {
        notify(
          t('errors.session_expired') || 'Sua sessão expirou. Por favor, faça login novamente. Seus dados estão salvos localmente.',
          'error'
        )
        // Don't clear autosave on auth error - data is safe in localStorage
      } else if (error.response?.status === 400) {
        // Bad request - validation error
        const errorMsg = error.response?.data?.error || 'Erro de validação'
        notify(errorMsg, 'error')
      } else {
        notify(t('actions.save_error'), 'error')
      }
    } finally {
      setIsCreatingOnePage(false)
      setIsEditingOnePage(false)
      setIsOnePageLoading(false)
    }
  }

  // Approval Drawer Functions
  const handleOpenApproveDrawer = () => {
    if (isCreatingOnePage) return
    setIsApproveDrawerOpen(true)
  }
  const handleCloseApproveDrawer = () => {
    setIsApproveDrawerOpen(false)
  }

  const handleApproveSave = async () => {
    if (!currentOnePage || !selectedApproval) {
      toast.warn(t('approvals.select_an_option'))
      return
    }

    setIsApproving(true)
    try {
      const payload = {
        onepage_id: currentOnePage.id,
        action: selectedApproval, // 'approved' ou 'disapproved' (ou 'rejected')
        comment: approveComment
      }

      const response = await performOnePageApprovalAction(payload)

      if (response && response.success) {
        toast.success(t('approvals.action_successful'))
        // Recarrega os dados para a UI refletir a mudança
        if (fetchOnePageData) {
          await fetchOnePageData()
        }
      } else {
        toast.error(response.error || t('errors.action_failed'))
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || t('errors.action_failed')
      // console.error('OnePage approval failed:', error)
      toast.error(errorMsg)
    } finally {
      setIsApproving(false)
      setIsApproveDrawerOpen(false) // Fecha o drawer
      setApproveComment('') // Limpa o estado
      setSelectedApproval(null) // Limpa a seleção
    }
  }

  // Toast notifiaction template
  const notify = (message, type = 'info') =>
    toast(message, {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: 'light',
      type: type,
      transition: ToastSlide
    })

  // ######====== API AND GENERAL FUNCTIONS
  // ALL HOOKS MOVED TO TOP TO FIX HOOK ORDER VIOLATION



  // ######====== RETURN
  // If showContent is false, render the ContentContainer for Meeting Management usage (no AppBar, nor Drawer, nor state funcs)
  if (!showContent) {
    // Show loading while initiative is being loaded
    if (!selectedInitiative) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <ThreeDots
            color={userDetails?.implementation_color || '#1976d2'}
            height={40}
            width={40}
            ariaLabel="loading"
          />

        </div>
      )
    }

    if (!selectedInitiative.id || selectedInitiative.initiative === "Loading...") {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <ThreeDots
            color={userDetails?.implementation_color || '#1976d2'}
            height={40}
            width={40}
            ariaLabel="loading"
          />

        </div>
      )
    }

    const renderEndTime = performance.now()


    return (
      <div style={{ padding: '16px 5px', marginBottom: '-15px' }}>
        <OnePageContentContainer
          isCreatingOnePage={false}
          currentOnePage={currentOnePage}
          selectedInitiative={selectedInitiative}
          newStatus={safeOnePage.status}
          setNewStatus={() => {}}
          newTendency={safeOnePage.tendency}
          setNewTendency={() => {}}
          newDate={safeOnePage.date}
          setNewDate={() => {}}
          newKm={safeOnePage.km}
          setNewKm={() => {}}
          newAch={safeOnePage.achievements}
          setNewAch={() => {}}
          newNextSteps={safeOnePage.next_steps}
          setNewNextSteps={() => {}}
          newHelpRequests={safeOnePage.help_requests}
          setNewHelpRequests={() => {}}
          classes={classes}
          t={t}
          selectedOnePageDate={selectedOnePageDate}
          renderStatusColor={renderStatusColor}
          lastOnePages={lastOnePages}
          newCustomFields={newCustomFields}
          setNewCustomFields={setNewCustomFields}
          textEditorKey={textEditorKey}
        />
      </div>
    )
  }

  // If showContent is true, render the full page:
  // Show loading while initiative is being loaded
  if (!selectedInitiative) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px'
      }}>
        <ThreeDots
          color={userDetails?.implementation_color || '#1976d2'}
          height={60}
          width={60}
          ariaLabel="loading"
        />

      </div>
    )
  }

  if (!selectedInitiative.id || selectedInitiative.initiative === "Loading...") {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px'
      }}>
        <ThreeDots
          color={userDetails?.implementation_color || '#1976d2'}
          height={60}
          width={60}
          ariaLabel="loading"
        />

      </div>
    )
  }

  const renderEndTime = performance.now()


  return (
    <div style={{ width: '100%' }}>
      {/* APP BAR Superior */}
      <OnePageAppBar
        isCreatingOnePage={isCreatingOnePage}
        isEditingOnePage={isEditingOnePage}
        newDate={newDate}
        canReport={canReport}
        canApprove={canApprove}
        handleSaveNewOnePage={handleSaveNewOnePage}
        handleOpenCancelDialog={handleOpenCancelDialog}
        handleEditOnePage={handleEditOnePage}
        handleCreateOnePage={handleCreateOnePage}
        isCancelDialogOpen={isCancelDialogOpen}
        handleCloseCancelDialog={handleCloseCancelDialog}
        handleConfirmCancel={handleConfirmCancel}
        selectedOnePageDate={selectedOnePageDate}
        setSelectedOnePageDate={setSelectedOnePageDate}
        onePageDateList={onePageDateList}
        userDetails={userDetails}
        onePageHasApproval={onePageHasApproval}
        handleOpenApproveDrawer={handleOpenApproveDrawer}
        t={t}
        classes={classes}
      />
      {/* Approval Banner Status */}
      {onePageHasApproval && !isCreatingOnePage && currentOnePage && (
        <OnePageApprovalBanner
          // Pass the specific approval status of the current OnePage
          approved={currentOnePage.approval_status || 'approved'} // 'pending' or 'approved'
          comment={currentOnePage.comment} // You can add rejection comments later
          t={t} // Pass translation function
        />
      )}

      {/* Floating notification for approver */}
      {canApprove && openApproverNotification && onePageHasApproval && (
        <Snackbar
          open={openApproverNotification}
          autoHideDuration={3000}
          onClose={() => setOpenApproverNotification(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          style={{ marginTop: '8px' }}
        >
          <div
            style={{
              backgroundColor: '#f7f7f7',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '4px 8px',
              display: 'flex',
              flexDirection: 'column',
              width: 'auto',
              boxSizing: 'border-box'
            }}
          >
            <Typography
              variant='body2'
              style={{ color: '#555', marginBottom: '4px' }}
            >
              {t('initiative_selected.approver_flag')}
            </Typography>
            <CustomLinearProgress
              style={{ margin: '0' }}
              variant='determinate'
              color='client'
              value={progress}
            />
          </div>
        </Snackbar>
      )}

      {/* AI Insights Floating Button - Memoized for performance */}
      {aiInsightsButton}

      {/* Speech Recorder Floating Button - Memoized for performance */}
      {speechRecorderButton}

      {/* Containerized content */}
      {isOnePageLoading ? (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <ThreeDots
            color={userDetails.implementation_color}
            height={80}
            width={80}
            ariaLabel='loading'
          />
        </div>
      ) : (
        <OnePageContentContainer
          isCreatingOnePage={isCreatingOnePage}
          currentOnePage={currentOnePage}
          selectedInitiative={selectedInitiative}
          newStatus={newStatus}
          setNewStatus={setNewStatus}
          newTendency={newTendency}
          setNewTendency={setNewTendency}
          newDate={newDate}
          setNewDate={setNewDate}
          newKm={newKm}
          setNewKm={setNewKm}
          newAch={newAch}
          setNewAch={setNewAch}
          newNextSteps={newNextSteps}
          setNewNextSteps={setNewNextSteps}
          newHelpRequests={newHelpRequests}
          setNewHelpRequests={setNewHelpRequests}
          classes={classes}
          t={t}
          selectedOnePageDate={selectedOnePageDate}
          renderStatusColor={renderStatusColor}
          lastOnePages={lastOnePages}
          newCustomFields={newCustomFields}
          setNewCustomFields={setNewCustomFields}
          textEditorKey={textEditorKey}
        />
      )}

      {/* Drawer Approvals */}
      {onePageHasApproval && (
        <OnePageDrawerApprovals
          isOpen={isApproveDrawerOpen}
          onClose={handleCloseApproveDrawer}
          onSave={handleApproveSave}
          selectedApproval={selectedApproval}
          setSelectedApproval={setSelectedApproval}
          approveComment={approveComment}
          setApproveComment={setApproveComment}
          t={t}
          classes={classes}
          isSaving={isApproving}
        />
      )}

      {/* AI Insights Modal */}
      <AIInsightsModal
        open={isInsightsModalOpen}
        onClose={handleCloseInsights}
        title="insights.modal_title"
        disclaimer="insights.ai_disclaimer"
        content={insightsContent}
        isLoading={isLoadingInsights}
        onCopy={handleCopyToClipboard}
        isCopying={isCopying}
        userDetails={userDetails}
        copyTooltipKey="insights.copy_to_clipboard"
        noContentKey="insights.no_content"
        closeLabelKey="Fechar"
        loadingComponent={
          <InitiativeInsightsLoading
            isLoading={isLoadingInsights}
            interval={2500}
            loadingComponent={
              <ThreeDots
                color={userDetails.implementation_color || '#1976d2'}
                height={80}
                width={80}
                ariaLabel="loading"
              />
            }
          />
        }
      />

      {/* Speech Recorder Modal */}
      <OnePageSpeechRecorder
        isOpen={isSpeechRecorderOpen}
        onClose={handleCloseSpeechRecorder}
        onSpeechProcessed={handleSpeechProcessed}
        selectedInitiative={selectedInitiative}
        t={t}
      />
    </div>
  )
}

// ######====== PROPS TYPE VALIDATION AND EXPORT DEFAULT
OnePage.propTypes = {
  selectedInitiative: PropTypes.object.isRequired,
  onePageInfoData: PropTypes.array, // Já é opcional, ok.

  // Funções podem ser opcionais, pois nem sempre são necessárias (modo visualização)
  fetchOnePageData: PropTypes.func,
  setOnePageInfoData: PropTypes.func,
  setSelectedOnePageDate: PropTypes.func,
  canReport: PropTypes.bool,
  canApprove: PropTypes.bool,
  onePageHasApproval: PropTypes.bool,
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  showContent: PropTypes.bool,
  defaultOnePageDate: PropTypes.string,
  selectedOnePageDate: PropTypes.string
}

OnePage.defaultProps = {
  showContent: true
}

export default OnePage
